// apps/admin/src/components/EntorsUploadSection.tsx
// Isolated Entors upload UI with RID-based pairing

import React, { useState, DragEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { functions, storage, auth } from '../firebaseConfig';
import { v4 as uuidv4 } from 'uuid';
import JSZip from 'jszip';

interface EntorsUploadSectionProps {
  companyId: string;
  offices: Array<{ id: string; name: string }>;
  entorsTemplateId: string;
  onUploadSuccess?: (result: { sourceDocId?: string }) => void;
  isOnboarding?: boolean;
}

interface ZipPreview {
  pdfCount: number;
  excelCount: number;
}

const EntorsUploadSection: React.FC<EntorsUploadSectionProps> = ({
  companyId,
  offices,
  entorsTemplateId,
  onUploadSuccess,
  isOnboarding = false
}) => {
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>(offices[0]?.id || '');
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [zipPreview, setZipPreview] = useState<ZipPreview | null>(null);

  // Preview ZIP contents for RID-based pairing
  const previewZipContents = async (file: File) => {
    try {
      const zip = new JSZip();
      const contents = await zip.loadAsync(file);

      let pdfCount = 0;
      let excelCount = 0;

      Object.keys(contents.files).forEach(filename => {
        const lowerName = filename.toLowerCase();

        // Skip directories and macOS artifacts
        if (contents.files[filename].dir || lowerName.includes('__macosx')) {
          return;
        }

        // Count PDFs
        if (lowerName.endsWith('.pdf')) {
          pdfCount++;
        }

        // Count Excel files
        if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
          excelCount++;
        }
      });

      setZipPreview({ pdfCount, excelCount });
    } catch (err) {
      console.error('Error previewing ZIP contents:', err);
      setError('Failed to preview ZIP contents. Please ensure the file is a valid ZIP archive.');
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0 && (files[0].type === "application/zip" || files[0].name.endsWith('.zip'))) {
      const selectedFile = files[0];
      setZipFile(selectedFile);
      setError(null);
      previewZipContents(selectedFile);
    } else {
      setError("Invalid file type. Please upload a .zip file.");
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];
      setZipFile(selectedFile);
      setError(null);
      await previewZipContents(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!zipFile) {
      setError("Please select a ZIP file to upload.");
      return;
    }
    if (!selectedOfficeId) {
      setError("Please select an office.");
      return;
    }
    if (zipFile.size > 50 * 1024 * 1024) {
      setError('File size cannot exceed 50MB.');
      return;
    }

    setLoading('Preparing upload...');
    setError(null);
    setUploadProgress(0);

    try {
      // CRITICAL: Verify authentication and refresh token before upload
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('Authentication required. Please refresh the page and log in again.');
        setLoading(null);
        return;
      }

      // Force token refresh to ensure it's valid and not expired
      try {
        const tokenResult = await currentUser.getIdTokenResult(true); // force refresh

        // Verify user has required role for supplemental uploads
        const hasFounderRole = tokenResult.claims.isFounder === true ||
                              tokenResult.claims.role === 'founder';
        if (!hasFounderRole) {
          setError('Insufficient permissions. Only founders can upload supplemental data.');
          setLoading(null);
          return;
        }

        console.log('[EntorsUpload] Auth token refreshed successfully', {
          uid: currentUser.uid,
          role: tokenResult.claims.role,
          expirationTime: tokenResult.expirationTime
        });
      } catch (tokenError) {
        console.error('[EntorsUpload] Token refresh failed:', tokenError);
        setError('Authentication token refresh failed. Please refresh the page and try again.');
        setLoading(null);
        return;
      }

      const taskId = uuidv4();
      const storagePath = `companies/${companyId}/supplemental/${taskId}/${zipFile.name}`;
      const storageRef = ref(storage, storagePath);

      const uploadTask = uploadBytesResumable(storageRef, zipFile);

      await new Promise<void>((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
            setLoading(`Uploading... ${Math.round(progress)}%`);
          },
          (error) => reject(error),
          () => resolve()
        );
      });

      const fullPath = uploadTask.snapshot.ref.fullPath;

      setLoading('Processing Entors ZIP with RID-based pairing...');

      const uploadSupplementalData = httpsCallable(functions, 'uploadSupplementalData');
      const result = await uploadSupplementalData({
        companyId,
        officeId: selectedOfficeId,
        fileMeta: {
          fileName: zipFile.name,
          mimeType: zipFile.type || 'application/zip',
          storagePath: fullPath,
        },
        templateId: entorsTemplateId,
        uploadMode: 'legacy' // Entors uses legacy mode with RID-based pairing
      });

      setLoading(null);
      setZipFile(null);
      setZipPreview(null);

      if (onUploadSuccess) {
        onUploadSuccess(result.data as any);
      }
    } catch (err: any) {
      setError(`Upload failed: ${err.message}`);
      setLoading(null);
    }
  };

  const inputClasses = "w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg placeholder-slate-500 text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500";
  const buttonClasses = "w-full py-3 font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-all";

  return (
    <div className="p-6 rounded-2xl shadow-xl bg-black/20 backdrop-blur-lg border border-white/10">
      <h3 className="text-xl font-semibold mb-1">Entors Upload</h3>
      <p className="text-slate-400 text-sm mb-6">Upload ZIP containing Entors PDF + Excel pairs (RID-based matching)</p>

      {error && (
        <div className="p-3 my-4 bg-red-500/20 border border-red-500/30 rounded-md text-center">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Office Selector - Hidden in onboarding mode */}
      {!isOnboarding && (
        <div className="mb-6">
          <label htmlFor="office" className="block text-sm font-medium text-slate-300 mb-1">
            Office
          </label>
          <select
            id="office"
            value={selectedOfficeId}
            onChange={e => setSelectedOfficeId(e.target.value)}
            className={inputClasses}
            disabled={offices.length === 0}
          >
            {offices.length === 0 && <option>Loading offices...</option>}
            {offices.map(office => (
              <option key={office.id} value={office.id}>{office.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Info Panel - RID-based pairing explanation */}
      <div className="mb-6 bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg">
        <h4 className="font-semibold mb-2 text-blue-300">📋 Entors RID-Based Pairing</h4>
        <ul className="space-y-1 text-sm text-slate-300">
          <li>✓ Upload ZIP containing PDF and Excel files</li>
          <li>✓ Files are automatically matched by RID (Report ID)</li>
          <li>✓ RID is extracted from file contents, not filenames</li>
          <li>✓ Entors always uses RUN 1 (full-month periods)</li>
          <li>✓ Max file size: 50MB per ZIP</li>
        </ul>
      </div>

      {/* ZIP Dropzone */}
      <div className="space-y-4">
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`flex justify-center px-6 pt-8 pb-8 border-2 border-slate-700 border-dashed rounded-md transition-colors ${isDragging ? 'bg-slate-800/50 border-fuchsia-500' : ''}`}
        >
          <div className="space-y-1 text-center">
            <div className="text-6xl mb-4">📦</div>
            <div className="flex text-lg text-slate-300 font-medium justify-center">
              <label htmlFor="entors-zipfile" className="relative cursor-pointer bg-slate-900 rounded-md font-medium text-indigo-400 hover:text-indigo-300">
                <span>{zipFile ? `Selected: ${zipFile.name}` : "Click to upload Entors ZIP"}</span>
                <input id="entors-zipfile" type="file" accept=".zip,application/zip,application/x-zip-compressed" onChange={handleFileSelect} className="sr-only" />
              </label>
            </div>
            <p className="text-sm text-slate-500">or drag and drop (max 50MB)</p>
            {zipFile && (
              <p className="text-sm text-slate-400 mt-2">{(zipFile.size / 1024 / 1024).toFixed(2)} MB</p>
            )}
          </div>
        </div>

        {/* ZIP Preview Results */}
        {zipPreview && (
          <div className="bg-green-900/20 border border-green-500/30 p-4 rounded-lg">
            <h4 className="font-semibold mb-2 text-green-300">✅ ZIP Contents Preview:</h4>
            <div className="grid grid-cols-2 gap-4 text-sm text-slate-300">
              <div>
                <span className="font-medium">📄 PDF Files:</span>
                <span className="ml-2">{zipPreview.pdfCount}</span>
              </div>
              <div>
                <span className="font-medium">📊 Excel Files:</span>
                <span className="ml-2">{zipPreview.excelCount}</span>
              </div>
            </div>
            {zipPreview.pdfCount > 0 && zipPreview.excelCount > 0 && zipPreview.pdfCount === zipPreview.excelCount ? (
              <p className="text-xs text-green-400 mt-2">
                ✅ {zipPreview.pdfCount} potential pairs detected. Backend will match by RID.
              </p>
            ) : zipPreview.pdfCount > 0 && zipPreview.excelCount > 0 ? (
              <p className="text-xs text-yellow-400 mt-2">
                ⚠️ Unequal file counts. Only matching RIDs will be paired.
              </p>
            ) : (
              <p className="text-xs text-red-400 mt-2">
                ❌ Both PDF and Excel files required for Entors uploads.
              </p>
            )}
            <p className="text-xs text-slate-500 mt-2">
              Run number: <span className="text-white">RUN 1</span> (Entors uses full-month periods)
            </p>
          </div>
        )}
      </div>

      {loading && <p className="text-center text-indigo-400 mt-4">{loading}</p>}

      {/* Upload Button - Hidden in onboarding mode */}
      {!isOnboarding && (
        <div className="mt-6">
          <button
            onClick={handleUpload}
            disabled={!!loading || !zipFile}
            className={buttonClasses}
          >
            {loading ? 'Uploading...' : 'Upload & Process Entors ZIP'}
          </button>
        </div>
      )}
    </div>
  );
};

export default EntorsUploadSection;
